import { z } from 'zod';

import {
  type ClientActionAgent,
  ClientActionAgentSchema,
} from './agent-activity';
import { type UiElementAccess, uiElementAccessSchema } from './ui-access';
import type {
  WorkspaceViewRevision,
  WorkspaceViewScope,
  WorkspaceViewSnapshot,
} from './workspace-view';
import { parseWorkspaceViewSnapshot } from './workspace-view';

/**
 * The AI operates a visible element of the user's current view like the user
 * would: clicking, double clicking or right clicking it, moving the pointer
 * over it, scrolling it, selecting text in it, typing into it, pressing a key
 * on it or dragging it, or filling several fields of one form at once. The
 * element is addressed by the key the view's snapshot listed; the browser
 * refuses a target that is no longer the one the AI saw.
 */
export const WORKSPACE_OPERATION_VERBS = [
  'click',
  'double',
  'context',
  'hover',
  'scroll',
  'select',
  'type',
  'press',
  'drag',
  'fill',
] as const;
export type WorkspaceOperationVerb = (typeof WORKSPACE_OPERATION_VERBS)[number];

/**
 * Why an operation was not done. The user reads a sentence per code; the error text next to it is
 * for the model only.
 */
export const WORKSPACE_OPERATION_FAILURE = {
  /** No browser tab of this chat is bound, so nothing can be operated. */
  tabUnbound: 'WORKSPACE_TAB_UNBOUND',
  /** The tab is bound, but the addressed element is not in the view it shows now. */
  notInView: 'WORKSPACE_ELEMENT_NOT_IN_VIEW',
  /** The browser did not take the input where the element is now. */
  blocked: 'WORKSPACE_OPERATION_BLOCKED',
  /** What is shown changed before the operation was done. */
  stale: 'WORKSPACE_VIEW_STALE',
  /** The call did not fit the tool, so nothing was operated. */
  invalidArguments: 'WORKSPACE_OPERATION_ARGUMENTS',
} as const;
/** What the model reads when no browser tab of the chat is bound (code tabUnbound). */
export const WORKSPACE_TAB_UNBOUND_ERROR =
  'Operating the app requires the bound interactive browser tab of this conversation; none is bound now.';
/** What the model reads when it asks for the view without a bound browser tab. */
export const WORKSPACE_VIEW_UNBOUND_ERROR =
  'Reading the current view requires the bound interactive browser tab of this conversation.';
export type WorkspaceOperationFailure =
  (typeof WORKSPACE_OPERATION_FAILURE)[keyof typeof WORKSPACE_OPERATION_FAILURE];

/** Keys without modifiers only: shortcuts (e.g. undo) stay out of reach. */
export const WORKSPACE_OPERATION_KEYS = [
  'Enter',
  'Escape',
  'Tab',
  'Space',
  'Backspace',
  'Delete',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
] as const;
export type WorkspaceOperationKey = (typeof WORKSPACE_OPERATION_KEYS)[number];

export const WORKSPACE_OPERATION_MAX_TEXT_LENGTH = 4_000;
/** A stretch of existing text that names a place in it. */
export const WORKSPACE_OPERATION_MAX_ANCHOR_LENGTH = 256;
/** How far one `scroll` moves, in CSS pixels per axis. */
export const WORKSPACE_OPERATION_MAX_SCROLL = 4_000;
/** How many fields one `fill` writes. */
export const WORKSPACE_OPERATION_MAX_FILL_FIELDS = 40;

/** One field of a `fill`: its key, its complete new value, and the field as the AI saw it. */
export interface WorkspaceOperationFill {
  elementKey: string;
  /** A box to tick takes `true` or `false`, a field with options one of them. */
  text: string;
  target?: WorkspaceOperationTarget;
}

/** The element as the AI saw it in the snapshot. */
export interface WorkspaceOperationTarget {
  role: string;
  label: string;
  access?: UiElementAccess;
}

/** A point in the viewport's CSS pixels. */
export interface UiPoint {
  x: number;
  y: number;
}

export interface WorkspaceOperationRequest extends WorkspaceViewScope {
  agent: ClientActionAgent;
  requestId: string;
  revision: WorkspaceViewRevision;
  elementKey: string;
  verb: WorkspaceOperationVerb;
  /**
   * For `type`: a field's complete new value, or the text typed behind the text
   * of something on a drawn surface (a note on a canvas, a paragraph of a page).
   * For `select`: the stretch of the element's text, there exactly once, that
   * is selected, or where the selection starts when `until` names its end.
   */
  text?: string;
  /**
   * For `select`: a second stretch of the element's text, there exactly once
   * behind `text`; the selection reaches from the start of `text` to its end.
   */
  until?: string;
  /**
   * For `type` into text on a drawn surface: a stretch of that text, there
   * exactly once, behind which the text is typed instead of behind all of it.
   */
  after?: string;
  /**
   * For `type` into text on a drawn surface: a stretch of that text, there
   * exactly once, which the typed text takes the place of; empty text takes it
   * away.
   */
  replace?: string;
  /** For `press`. */
  key?: WorkspaceOperationKey;
  /**
   * Where on the element, e.g. a free spot of a canvas to click, or to start
   * new text at. Never for `press` and `select`.
   */
  at?: UiPoint;
  /** For `scroll`: how far the content moves, in CSS pixels; never both zero. */
  by?: UiPoint;
  /** For `drag`: where the pointer is released. */
  to?: UiPoint;
  /**
   * For `fill`: the fields of one form, each with its complete value, written
   * one after the other in one operation; `elementKey` is the first of them.
   * Either every field takes its value or none is touched.
   */
  fields?: WorkspaceOperationFill[];
  target: WorkspaceOperationTarget;
}

export type WorkspaceOperationOutcome =
  | { status: 'done'; context: WorkspaceViewSnapshot }
  | { status: 'blocked' | 'stale' | 'offline' | 'timeout'; messageKey: string };

/** Pending operation travels through the existing Copilot client-action stream. */
export interface WorkspaceOperationPending {
  kind: 'workspace-operation';
  status: 'pending';
  endpoint: string;
  timeoutMs: number;
  clientInstanceId: string;
  request: WorkspaceOperationRequest;
}
export interface WorkspaceOperationAck {
  kind: 'workspace-operation';
  clientInstanceId: string;
  requestId: string;
  workspaceId: string;
  outcome: WorkspaceOperationOutcome;
}

const coordinate = z.number().finite().min(-1_000_000).max(1_000_000);
export const uiPointSchema: z.ZodType<UiPoint> = z
  .object({ x: coordinate, y: coordinate })
  .strict();
const scrollDistance = z
  .number()
  .finite()
  .min(-WORKSPACE_OPERATION_MAX_SCROLL)
  .max(WORKSPACE_OPERATION_MAX_SCROLL);

export const workspaceOperationTargetSchema: z.ZodType<WorkspaceOperationTarget> =
  z
    .object({
      role: z.string().min(1).max(64),
      label: z.string().max(1024),
      access: uiElementAccessSchema.optional(),
    })
    .strict();

/** One field of a `fill`: its key in the view and its complete new value. */
const fillFieldSchema = z
  .object({
    elementKey: z.string().min(1).max(1024),
    text: z.string().max(WORKSPACE_OPERATION_MAX_TEXT_LENGTH),
    target: workspaceOperationTargetSchema.optional(),
  })
  .strict();

const operationFields = {
  workspaceId: z.string().min(1).max(256),
  viewId: z.string().min(1).max(256),
  revision: z
    .object({
      epoch: z.string().min(1).max(128),
      sequence: z.number().int().nonnegative().safe(),
    })
    .strict(),
  elementKey: z.string().min(1).max(1024),
  verb: z.enum(WORKSPACE_OPERATION_VERBS),
  text: z.string().max(WORKSPACE_OPERATION_MAX_TEXT_LENGTH).optional(),
  after: z
    .string()
    .min(1)
    .max(WORKSPACE_OPERATION_MAX_ANCHOR_LENGTH)
    .optional(),
  replace: z
    .string()
    .min(1)
    .max(WORKSPACE_OPERATION_MAX_ANCHOR_LENGTH)
    .optional(),
  until: z
    .string()
    .min(1)
    .max(WORKSPACE_OPERATION_MAX_ANCHOR_LENGTH)
    .optional(),
  key: z.enum(WORKSPACE_OPERATION_KEYS).optional(),
  at: uiPointSchema.optional(),
  to: uiPointSchema.optional(),
  by: z.object({ x: scrollDistance, y: scrollDistance }).strict().optional(),
  fields: z
    .array(fillFieldSchema)
    .min(1)
    .max(WORKSPACE_OPERATION_MAX_FILL_FIELDS)
    .optional(),
};
const verbArguments = (input: {
  verb: WorkspaceOperationVerb;
  text?: string;
  after?: string;
  replace?: string;
  until?: string;
  key?: WorkspaceOperationKey;
  at?: UiPoint;
  to?: UiPoint;
  by?: UiPoint;
  elementKey: string;
  fields?: readonly { elementKey: string }[];
}) =>
  (input.verb === 'fill') === (input.fields !== undefined) &&
  (input.verb !== 'fill' ||
    (input.at === undefined &&
      // zod still runs this check when `fields` broke its own bounds (empty).
      input.fields![0]?.elementKey === input.elementKey &&
      new Set(input.fields!.map(field => field.elementKey)).size ===
        input.fields!.length)) &&
  (input.verb === 'type' || input.verb === 'select') ===
    (input.text !== undefined) &&
  (input.verb !== 'select' ||
    (input.text!.length > 0 &&
      input.text!.length <= WORKSPACE_OPERATION_MAX_ANCHOR_LENGTH)) &&
  (input.verb === 'type' ||
    (input.after === undefined && input.replace === undefined)) &&
  (input.after === undefined || input.replace === undefined) &&
  (input.verb === 'select' || input.until === undefined) &&
  (input.verb === 'press') === (input.key !== undefined) &&
  (input.verb === 'drag') === (input.to !== undefined) &&
  (input.verb === 'scroll') === (input.by !== undefined) &&
  (input.by === undefined || input.by.x !== 0 || input.by.y !== 0) &&
  (input.at === undefined ||
    (input.verb !== 'press' && input.verb !== 'select'));
const verbArgumentsMessage = {
  message:
    '`type` takes `text` and at most one of `after` and `replace`, `select` takes `text` and optionally `until`, `press` takes `key`, `drag` takes `to`, `scroll` takes `by`, `fill` takes `fields` (distinct keys, the first one the `elementKey`); `at` never for `press`, `select` and `fill`',
};

/**
 * The operation call. `target` belongs to the server: it resolves the element
 * from the view snapshot the AI saw and overwrites whatever the call carried,
 * so a confirmed call runs against the element the user approved.
 */
export const workspaceOperationInputSchema = z
  .object({
    ...operationFields,
    target: workspaceOperationTargetSchema.optional(),
  })
  .strict()
  .refine(verbArguments, verbArgumentsMessage);
export type WorkspaceOperationInput = z.infer<
  typeof workspaceOperationInputSchema
>;

export type PreparedWorkspaceOperationRequest = Omit<
  WorkspaceOperationRequest,
  'agent'
>;

const operationPreparationSchema = z
  .object({
    ...operationFields,
    requestId: z.string().trim().min(1).max(256),
    target: workspaceOperationTargetSchema,
  })
  .strict();

const operationRequestSchema = operationPreparationSchema
  .extend({ agent: ClientActionAgentSchema })
  .refine(verbArguments, verbArgumentsMessage);

export function parsePreparedWorkspaceOperationRequest(
  value: unknown,
  expectedWorkspaceId: string
): PreparedWorkspaceOperationRequest | null {
  const parsed = operationPreparationSchema
    .refine(verbArguments, verbArgumentsMessage)
    .safeParse(value);
  return parsed.success && parsed.data.workspaceId === expectedWorkspaceId
    ? parsed.data
    : null;
}

/** Accepts only a well-formed operation request for the expected workspace. */
export function parseWorkspaceOperationRequest(
  value: unknown,
  expectedWorkspaceId: string
): WorkspaceOperationRequest | null {
  const parsed = operationRequestSchema.safeParse(value);
  return parsed.success && parsed.data.workspaceId === expectedWorkspaceId
    ? parsed.data
    : null;
}

const failedStatusSchema = z.enum(['blocked', 'stale', 'offline', 'timeout']);
const MESSAGE_KEY_MAX_LENGTH = 256;

/** Accepts only a bounded snapshot of the expected workspace or a keyed failure. */
export function parseWorkspaceOperationOutcome(
  value: unknown,
  expectedWorkspaceId: string
): WorkspaceOperationOutcome | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const outcome = value as Record<string, unknown>;
  if (outcome.status === 'done') {
    const parsed = parseWorkspaceViewSnapshot(
      outcome.context,
      expectedWorkspaceId
    );
    return parsed.ok ? { status: 'done', context: parsed.snapshot } : null;
  }
  const status = failedStatusSchema.safeParse(outcome.status);
  const messageKey = outcome.messageKey;
  if (
    !status.success ||
    typeof messageKey !== 'string' ||
    !messageKey.trim() ||
    messageKey.length > MESSAGE_KEY_MAX_LENGTH
  )
    return null;
  return { status: status.data, messageKey };
}
